import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Vendor } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupVendorsProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const vendorTypeOptions = [
  { value: 'process-server', label: 'Process Server' },
  { value: 'courier', label: 'Courier' },
  { value: 'skip-trace', label: 'Skip Trace' },
  { value: 'research', label: 'Research' },
  { value: 'other', label: 'Other' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const SetupVendors: React.FC<SetupVendorsProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<Vendor>>({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchVendors = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const data = await get<{ data: Vendor[] }>(`/api/admin/setup/vendors?${params}`);
      setVendors(data.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchVendors();
  }, [fetchVendors]);

  const handleRowClick = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setFormData(vendor);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedVendor(null);
    setFormData({
      vendorCode: '',
      name: '',
      vendorType: 'process-server',
      email: '',
      status: 'active',
      serviceRegions: [],
    });
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street1: prev.address?.street1 || '',
          city: prev.address?.city || '',
          state: prev.address?.state || '',
          zip: prev.address?.zip || '',
          country: prev.address?.country || 'US',
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await post('/api/admin/setup/vendors', formData);
      } else {
        await patch(`/api/admin/setup/vendors/${selectedVendor?.id}`, formData);
      }

      setIsDrawerOpen(false);
      fetchVendors();
    } catch (error) {
      console.error('Failed to save vendor:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      key: 'vendorCode',
      header: 'Code',
      width: '120px',
      render: (vendor: Vendor) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: brand.colors.link }}>
            {vendor.vendorCode}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={vendor.name}
            fields={[
              { label: 'Code', value: vendor.vendorCode },
              { label: 'Type', value: vendor.vendorType.replace('-', ' ') },
              { label: 'Email', value: vendor.email },
              { label: 'Phone', value: vendor.phone || '-' },
              { label: 'Rate', value: vendor.standardRate ? `$${vendor.standardRate.toFixed(2)}` : '-' },
            ]}
          />
        </div>
      ),
    },
    { key: 'name', header: 'Name' },
    {
      key: 'vendorType',
      header: 'Type',
      render: (vendor: Vendor) => (
        <span className="capitalize">{vendor.vendorType.replace('-', ' ')}</span>
      ),
    },
    { key: 'email', header: 'Email' },
    { key: 'phone', header: 'Phone' },
    {
      key: 'serviceRegions',
      header: 'Regions',
      render: (vendor: Vendor) => (
        <span>{vendor.serviceRegions.map(r => r.state).join(', ') || '-'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (vendor: Vendor) => <StatusBadge status={vendor.status} />,
    },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search vendors..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        onAdd={handleAdd}
        addLabel="Add Vendor"
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={vendors}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No vendors found"
        getRowId={(vendor) => vendor.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'Add Vendor' : 'Edit Vendor'}
        subtitle={selectedVendor?.vendorCode}
        brand={brand}
        Icons={Icons}
        width="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Vendor Code"
              name="vendorCode"
              value={formData.vendorCode || ''}
              onChange={handleInputChange}
              required
              placeholder="e.g., PSV-001"
            />
            <FormSelect
              brand={brand}
              label="Type"
              name="vendorType"
              value={formData.vendorType || 'process-server'}
              onChange={handleInputChange}
              options={vendorTypeOptions}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Name"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              required
              placeholder="Vendor name"
            />
            <FormInput
              brand={brand}
              label="Company Name"
              name="companyName"
              value={formData.companyName || ''}
              onChange={handleInputChange}
              placeholder="Company name (if different)"
            />
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Contact Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Contact Name"
                name="contactName"
                value={formData.contactName || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Fax"
                name="fax"
                type="tel"
                value={formData.fax || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Licensing & Insurance
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="License Number"
                name="licenseNumber"
                value={formData.licenseNumber || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="License Expiration"
                name="licenseExpiration"
                type="date"
                value={formData.licenseExpiration?.split('T')[0] || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Bonded Amount"
                name="bondedAmount"
                type="number"
                value={formData.bondedAmount || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Insurance Expiration"
                name="insuranceExpiration"
                type="date"
                value={formData.insuranceExpiration?.split('T')[0] || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Rates
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Standard Rate ($)"
                name="standardRate"
                type="number"
                value={formData.standardRate || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Rush Rate ($)"
                name="rushRate"
                type="number"
                value={formData.rushRate || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <FormSelect
              brand={brand}
              label="Status"
              name="status"
              value={formData.status || 'active'}
              onChange={handleInputChange}
              options={statusOptions}
            />
            <FormTextarea
              brand={brand}
              label="Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={3}
            />
          </div>
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupVendors;
